<template>
  <div class="modal-mask" @click.self="$emit('close')">
    <div class="modal-container">
      <h3 class="modal-title">Encaminhar Mensagens</h3>
      
      <div class="selected-info">
        <span class="badge">{{ selectedMessages.length > 0 ? selectedMessages.length : 'Toda a conversa' }} selecionada</span>
      </div>

      <!-- Seleção de Tipo -->
      <div class="type-selector">
        <label class="radio-label">
          <input type="radio" v-model="forwardType" value="whatsapp" @change="resetSearch" />
          Contatos
        </label>
        <label class="radio-label">
          <input type="radio" v-model="forwardType" value="agent" @change="resetSearch" />
          Agentes
        </label>
        <label class="radio-label">
          <input type="radio" v-model="forwardType" value="manual" @change="resetSearch" />
          Novo Número
        </label>
      </div>

      <!-- Busca Dinâmica -->
      <div v-if="forwardType !== 'manual'" class="field">
        <label class="label-text">{{ forwardType === 'whatsapp' ? 'Pesquisar Contato' : 'Pesquisar Agente' }}</label>
        <div class="search-container">
          <input 
            v-model="searchQuery" 
            type="text" 
            :placeholder="forwardType === 'whatsapp' ? 'Nome ou telefone...' : 'Nome do agente...'" 
            class="input custom-input"
            @input="onSearchInput"
          />
          <div v-if="isSearching" class="loader-inline">Buscando...</div>
        </div>
        
        <ul v-if="results.length > 0" class="contact-list shadow-lg">
          <li 
            v-for="item in results" 
            :key="item.id" 
            @click="selectItem(item)"
            class="contact-item"
          >
            <div class="contact-name">{{ item.name }}</div>
            <div class="contact-phone">{{ forwardType === 'whatsapp' ? item.phone_number : item.email }}</div>
          </li>
        </ul>
      </div>

      <!-- Campo de Digitação Manual -->
      <div v-if="forwardType === 'manual'" class="field">
        <label class="label-text">Digite o Número do WhatsApp</label>
        <input 
          v-model="destinationValue" 
          type="text" 
          placeholder="Ex: 5511999999999" 
          class="input highlight-input"
          @input="destinationDisplay = destinationValue"
        />
        <p class="help-text">Informe DDI + DDD + Número (apenas números).</p>
      </div>

      <hr class="separator" />

      <!-- Valor de Destino Final -->
      <div v-if="forwardType !== 'manual'" class="field">
        <label class="label-text">Destino Selecionado:</label>
        <input 
          v-model="destinationDisplay" 
          type="text" 
          readonly
          placeholder="Selecione na lista acima..." 
          class="input readonly-input" 
        />
      </div>

      <div class="modal-footer">
        <button 
          :loading="loading" 
          @click="send" 
          class="bg-n-brand"
          :disabled="!destinationValue"
        >
          <span style="color: white;">{{ forwardType === 'agent' ? 'Encaminhar p/ Agente' : 'Encaminhar p/ WhatsApp' }}</span>
        </button>
        <button variant="clear" @click="$emit('close')">Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { emitter } from 'shared/helpers/mitt';

export default {
  props: { selectedMessages: Array },
  data() {
    return {
      forwardType: 'whatsapp',
      destinationValue: '',
      destinationDisplay: '',
      searchQuery: '',
      results: [],
      isSearching: false,
      loading: false,
      searchTimeout: null
    };
  },
  computed: {  
    accountIdFromRoute() {
      const pathSegments = this.$route.path.split('/');
      const accountIndex = pathSegments.indexOf('accounts');
      return accountIndex !== -1 ? pathSegments[accountIndex + 1] : '';
    }
  },
  unmounted() {
    emitter.emit('dex-forward-message-close');
  },
  methods: { 
    resetSearch() {
      this.searchQuery = '';
      this.results = [];
      this.destinationValue = '';
      this.destinationDisplay = '';
    },
    onSearchInput() {
      if (this.searchQuery.length < 2) {
        this.results = [];
        return;
      }
      this.isSearching = true;
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        if (this.forwardType === 'whatsapp') { this.fetchContacts(); } 
        else if (this.forwardType === 'agent') { this.fetchAgents(); }
      }, 400);
    },
    async fetchContacts() {
      try {
        const response = await axios.get(`/api/v1/accounts/${this.accountIdFromRoute}/contacts/search`, {
          params: { q: this.searchQuery }
        });
        this.results = response.data.payload.filter(c => c.phone_number);
      } catch (e) { console.error(e); } finally { this.isSearching = false; }
    },
    async fetchAgents() {
      try {
        const response = await axios.get(`/api/v1/accounts/${this.accountIdFromRoute}/agents`);
        const query = this.searchQuery.toLowerCase();
        this.results = response.data.filter(a => a.name.toLowerCase().includes(query));
      } catch (e) { console.error(e); } finally { this.isSearching = false; }
    },
    selectItem(item) {
      if (this.forwardType === 'whatsapp') {
        this.destinationValue = item.phone_number;
        this.destinationDisplay = `${item.name} (${item.phone_number})`;
      } else {
        this.destinationValue = item.id;
        this.destinationDisplay = `${item.name} (Agente)`;
      }
      this.results = [];
      this.searchQuery = item.name;
    },
    async send() {
      if(!this.destinationValue) return;
      let ids = this.selectedMessages.map(msg => msg.messageId);
      if (ids.length === 0) {
        const confirmAll = window.confirm("Nenhuma mensagem selecionada. Deseja encaminhar TODAS as mensagens desta conversa?");
        if (!confirmAll) return;
        const conversationId = this.$route.params.conversationId;
        const allMessages = this.$store.getters['conversations/getConversationMessages'](conversationId);
        ids = allMessages.filter(m => !m.private && m.message_type !== 2).map(m => m.id);
        if (ids.length === 0) { alert("Nenhuma mensagem válida encontrada."); return; }
      }
      this.loading = true;
      const payloadType = (this.forwardType === 'agent') ? 'internal_agent' : 'external_whatsapp';
      try {
        await axios.post(`/api/v1/accounts/${this.accountIdFromRoute}/forwards`, {
          message_ids: ids,
          destinations: [{ type: payloadType, value: this.destinationValue }]
        });
        alert("Encaminhado com sucesso!");
        this.$emit('close');
      } catch (e) { alert("Erro ao enviar"); } finally { this.loading = false; }
    }
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed; z-index: 1000; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.6); display: flex; align-items: center; justify-content: center;
}

.modal-container {
  background-color: #ffffff;
  padding: 2rem; border-radius: 12px; width: 480px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-height: 90vh; overflow-y: auto;
  color: #1e293b;
}

.dark .modal-container {
  background-color: #151b22;
  border: 1px solid #30363d;
  color: #c9d1d9;
}

.modal-title { color: inherit; margin-bottom: 1rem; font-weight: 700; }

.type-selector {
  display: flex; gap: 12px; margin-bottom: 20px;
  background: #f8f9fa; padding: 8px; border-radius: 10px; justify-content: space-around;
}
.dark .type-selector { background: #0d1117; }

.radio-label {
  display: flex; align-items: center; gap: 5px; font-weight: 600;
  cursor: pointer; font-size: 13px; color: #475569;
}
.dark .radio-label { color: #8b949e; }

.field { margin-bottom: 1.2rem; position: relative; }
.label-text { font-size: 13px; font-weight: 500; }

.input {
  width: 100%; margin-top: 8px; padding: 12px;
  border: 1px solid #e2e8f0; border-radius: 8px; font-size: 14px;
  background: #ffffff; color: #1e293b;
}
.dark .input { background: #0d1117; border-color: #30363d; color: #c9d1d9; }

.highlight-input {
  border-color: #007bff; background-color: #f0f7ff;
  font-weight: bold; font-size: 16px !important; color: #0056b3;
}
.dark .highlight-input { background-color: rgba(31, 111, 235, 0.1); color: #58a6ff; border-color: #388bfd; }

.readonly-input { background-color: #f1f5f9; color: #64748b; cursor: not-allowed; }
.dark .readonly-input { background-color: #161b22; color: #484f58; }

.contact-list {
  background: #ffffff; border: 1px solid #e2e8f0;
  border-top: none; max-height: 180px; overflow-y: auto; list-style: none;
  padding: 0; margin: 0; border-radius: 0 0 8px 8px; position: absolute;
  width: 100%; z-index: 20;
}
.dark .contact-list { background: #161b22; border-color: #30363d; }

.contact-item { padding: 12px; cursor: pointer; border-bottom: 1px solid #f1f5f9; }
.dark .contact-item { border-bottom-color: #21262d; }
.contact-item:hover { background: #f8fafc; }
.dark .contact-item:hover { background: #1f2937; }

.contact-name { font-weight: 600; font-size: 13px; color: #1e293b; }
.dark .contact-name { color: #f0f6fc; }
.contact-phone { font-size: 12px; color: #64748b; }

.separator { border: 0; border-top: 1px solid #f1f5f9; margin: 1.2rem 0; }
.dark .separator { border-top-color: #21262d; }

.help-text { font-size: 11px; color: #94a3b8; margin-top: 5px; }

.badge {
  background: #007bff; color: white; padding: 5px 12px;
  border-radius: 20px; font-size: 12px; margin-bottom: 1.5rem;
  display: inline-block; font-weight: 600;
}

.modal-footer {
  margin-top: 1.5rem; display: flex; justify-content: flex-end; gap: 10px;
  padding-top: 1.2rem; border-top: 1px solid #f1f5f9;
}
.dark .modal-footer { border-top-color: #21262d; }

.loader-inline { position: absolute; right: 12px; top: 40px; font-size: 11px; color: #007bff; }
</style>